/**
 * Utility functions for debugging position-related issues in the workflow canvas
 */

// Add a throttling mechanism
let lastLogTime = 0;
const LOG_THROTTLE_MS = 500; // Only log position changes every 500ms

/**
 * Log node position with a standardized format
 */
export function logNodePosition(context: string, nodeId: string, position: any) {
  // Skip logging for certain high-frequency contexts
  if (context === 'NODE_CHANGE' || context === 'NODE_DRAG_STOP') {
    // Only log these events occasionally
    const now = Date.now();
    if (now - lastLogTime < LOG_THROTTLE_MS) {
      return; // Skip this log
    }
    lastLogTime = now;
  }
  
  console.log(`📍 [${context}] NODE[${nodeId}] POSITION: ${JSON.stringify(position)}`);
}

/**
 * Validate that a position is valid (has numeric x and y coordinates)
 */
export function validatePosition(position: any, nodeId: string, context: string): boolean {
  const isValid = position && 
                 typeof position.x === 'number' && 
                 typeof position.y === 'number' &&
                 !isNaN(position.x) && 
                 !isNaN(position.y);
  
  if (!isValid) {
    console.error(`❌ INVALID POSITION in ${context} for node ${nodeId}:`, position);
  }
  
  return isValid;
}

/**
 * Check if positions are preserved between original and loaded nodes
 */
export function checkPositionPreservation(originalNodes: any[], loadedNodes: any[]) {
  console.log("🔍 CHECKING POSITION PRESERVATION");
  
  originalNodes.forEach(originalNode => {
    const loadedNode = loadedNodes.find(n => n.id === originalNode.id);
    
    if (loadedNode) {
      const originalPos = originalNode.position;
      const loadedPos = loadedNode.position;
      
      if (originalPos && loadedPos) {
        if (originalPos.x !== loadedPos.x || originalPos.y !== loadedPos.y) {
          console.error(`❌ POSITION MISMATCH for node ${originalNode.id}:`, {
            original: originalPos,
            loaded: loadedPos
          });
        }
      } else {
        console.error(`❌ MISSING POSITION for node ${originalNode.id}:`, {
          original: originalPos,
          loaded: loadedPos
        });
      }
    }
  });
}

/**
 * Log all node positions in a collection
 */
export function logAllNodePositions(context: string, nodes: any[]) {
  // Skip some contexts to reduce noise
  if (context === 'NODE_CHANGE' || context === 'APPLY_LAYOUT_START') {
    // Only log summary for these contexts
    console.log(`📊 [${context}] ALL NODE POSITIONS: ${nodes.length} nodes`);
    return;
  }
  
  // For important contexts, log full details
  console.log(`📊 [${context}] ALL NODE POSITIONS:`, 
    JSON.stringify(nodes.map(node => ({
      id: node.id,
      type: node.type,
      position: node.position,
      valid: validatePosition(node.position, node.id, context)
    })), null, 2)
  );
}
